import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Shield, ArrowRight } from "lucide-react";
import { Button, Badge } from "@/components/kit";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";

const PLANS = [
  { name: "Free", price: 0, yearlyPrice: 0, credits: "50", sources: "Google Maps", blast: false, cta: "Mulai Gratis", highlight: false },
  { name: "Starter", price: 99000, yearlyPrice: 79200, credits: "1.000", sources: "+ Facebook & Instagram", blast: true, cta: "Pilih Starter", highlight: false },
  { name: "Pro", price: 249000, yearlyPrice: 199200, credits: "3.000", sources: "+ Facebook & Instagram", blast: true, cta: "Pilih Pro", highlight: true },
  { name: "Agency", price: 599000, yearlyPrice: 479200, credits: "8.000", sources: "+ Facebook & Instagram", blast: true, cta: "Pilih Agency", highlight: false },
];

const FAQS = [
  { q: "Apa itu kredit?", a: "1 kredit = 1 lead yang berhasil di-scrape. Paket Free mendapat 50 kredit gratis untuk mencoba." },
  { q: "Bagaimana cara top-up kredit?", a: "Masuk ke menu Billing, pilih jumlah kredit, bayar via Midtrans/iPaymu. Kredit rollover." },
  { q: "Apakah ada kontrak?", a: "Tidak ada kontrak. Cancel kapan saja dari halaman Billing." },
  { q: "Apakah data kartu aman?", a: "Ya. Kami tidak menyimpan data kartu. Pembayaran ditangani Midtrans/iPaymu." },
];

export default function PricingPage() {
  const [yearly, setYearly] = useState(false);

  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <Seo
        title="Harga RESADA — Paket Lead Generation & WhatsApp Blast Terjangkau"
        description="Lihat harga paket RESADA: mulai gratis hingga Rp599.000/bulan. 1 kredit = 1 lead. Hemat 20% dengan paket tahunan."
        path="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          name: "Paket RESADA",
          itemListElement: PLANS.filter(p => p.price > 0).map(p => ({
            "@type": "Offer", name: `Paket ${p.name}`, price: p.price, priceCurrency: "IDR",
          })),
        }}
      />
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo to="/" iconSize={32} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-sky-600">Beranda</Link>
            <Link to="/#fitur" className="hover:text-sky-600">Fitur</Link>
            <Link to="/pricing" className="text-sky-600">Harga</Link>
            <Link to="/blog" className="hover:text-sky-600">Blog</Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm">Masuk</Button></Link>
            <Link to="/register"><Button size="sm">Daftar Gratis</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-8 text-center">
        <Badge color="sky" className="mb-4">Harga Transparan</Badge>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold">
          Harga Lead Generation <span className="text-sky-500">Terjangkau</span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-lg">
          Bayar sesuai pemakaian. 1 kredit = 1 lead. Tanpa kontrak, bisa mulai gratis.
        </p>
        <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-sky-500" /> Garansi uang kembali 7 hari untuk pembelian pertama
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-6 text-center mb-12">
        <div className="inline-flex items-center gap-3 bg-slate-50 rounded-full px-6 py-3">
          <span className={`text-sm font-semibold ${!yearly ? "text-sky-600" : "text-slate-400"}`}>Bulanan</span>
          <button onClick={() => setYearly(v => !v)} className={`relative w-12 h-6 rounded-full transition ${yearly ? "bg-sky-500" : "bg-slate-300"}`} aria-label="Toggle tahunan">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${yearly ? "translate-x-6" : ""}`} />
          </button>
          <span className={`text-sm font-semibold ${yearly ? "text-sky-600" : "text-slate-400"}`}>
            Tahunan <span className="ml-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">Hemat 20%</span>
          </span>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div key={p.name} className={`rounded-2xl p-7 border transition ${p.highlight ? "bg-sky-500 text-white border-sky-500 sky-glow scale-[1.03]" : "bg-white border-slate-200"}`}>
              {p.highlight && <Badge color="green" className="mb-3">Paling Populer</Badge>}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <p className="mt-3">
                <span className="font-display text-4xl font-extrabold">
                  {p.price === 0 ? "Gratis" : `Rp${yearly ? p.yearlyPrice.toLocaleString("id-ID") : p.price.toLocaleString("id-ID")}`}
                </span>
                {p.price > 0 && <span className={p.highlight ? "text-sky-100" : "text-slate-400"}>/bln</span>}
              </p>
              {yearly && p.price > 0 && (
                <p className={`text-xs mt-1 line-through ${p.highlight ? "text-sky-200" : "text-slate-400"}`}>
                  Rp{p.price.toLocaleString("id-ID")}/bln
                </p>
              )}
              <ul className={`mt-6 space-y-3 text-sm ${p.highlight ? "text-sky-50" : "text-slate-600"}`}>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.credits} kredit/bulan</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.sources}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.blast ? "WA Blast (BYOK) — tanpa batas" : "WA Blast tidak termasuk"}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.blast ? "Email Blast via Gmail" : "Email Blast tidak termasuk"}</li>
              </ul>
              <Link to="/register" className="block mt-7">
                <Button className="w-full" variant={p.highlight ? "dark" : "outline"}>{p.cta} <ArrowRight className="w-4 h-4" /></Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="font-display text-2xl font-extrabold text-center mb-8">Pertanyaan Harga</h2>
        <div className="space-y-4">
          {FAQS.map((faq) => (
            <div key={faq.q} className="card-soft p-5">
              <h3 className="font-semibold text-slate-800">{faq.q}</h3>
              <p className="text-sm text-slate-600 mt-2">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
